/* eslint-disable array-callback-return */
import { useMsal } from "@azure/msal-react";
import {
  Icon,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  Text,
  TooltipHost,
} from "@fluentui/react";
import { useEffect, useState } from "react";
import { Tree, TreeNode } from "react-organizational-chart";
import Select, { SingleValue } from "react-select";
import styled from "styled-components";
import { apiScopes } from "../authConfig";
import { department } from "../objects/department";
import {
  getDepartment,
  getDepartmentChildren,
} from "../services/resource-manager";

interface departmentSelectorProps {
  departmentCode: string;
  resourceId: string;
  updateFunction: (
    departmentCode: string,
    resourceId: string,
    isConnected: boolean
  ) => void;
  isM365: boolean;
  defaultDepartmentList: Array<department>;
}

interface OptionType {
  label: string;
  value: string;
}

// ─── Styled Components ───────────────────────────────────────────────────────

const Wrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #eff6fc;
  border: 1px solid #c7e0f4;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 16px;
`;

const InfoBannerTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #0046c8;
  margin-bottom: 2px;
`;

const InfoBannerText = styled.div`
  font-size: 12px;
  color: #323130;
  line-height: 1.5;
`;

/** Two-column grid: left = form, right = org chart */
const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 16px;
  align-items: start;
  width: 100%;
  box-sizing: border-box;
`;

const SectionCard = styled.div`
  background: #ffffff;
  border: 1px solid #e1e4e8;
  border-radius: 8px;
  padding: 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07);
  box-sizing: border-box;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #323130;
  margin-bottom: 3px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SectionSubtitle = styled.div`
  font-size: 11px;
  color: #605e5c;
  margin-bottom: 14px;
  line-height: 1.4;
`;

const FieldWrapper = styled.div`
  margin-bottom: 14px;
`;

const FieldLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 600;
  color: #323130;
  margin-bottom: 5px;
`;

const HelpText = styled.div`
  font-size: 11px;
  color: #605e5c;
  margin-top: 4px;
`;

const TreeWrapper = styled.div`
  overflow-x: auto;
  overflow-y: visible;
  padding: 16px 8px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e1e4e8;
`;

const StyledNode = styled.div`
  padding: 6px 10px;
  border-radius: 6px;
  display: inline-block;
  border: 1px solid #0046c8;
  font-size: 11px;
  background: #fff;
  color: #323130;
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TreeLegendRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const TreeLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #605e5c;
`;

const LegendDot = styled.div<{ bg: string; border: string }>`
  width: 11px;
  height: 11px;
  border-radius: 3px;
  background: ${(p) => p.bg};
  border: 1px solid ${(p) => p.border};
  flex-shrink: 0;
`;

const Divider = styled.div`
  height: 1px;
  background: #e1e4e8;
  margin: 14px 0;
`;

// ─── Select shared styles ─────────────────────────────────────────────────────
const selectStyles = (disabled = false) => ({
  control: (base: any) => ({
    ...base,
    borderColor: "#8a8886",
    borderRadius: 4,
    minHeight: 34,
    fontSize: 13,
    cursor: disabled ? "not-allowed" : "default",
    "&:hover": {
      borderColor: disabled ? "#8a8886" : "#0046c8",
    },
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 13,
    backgroundColor: state.isSelected
      ? "#0046c8"
      : state.isFocused
      ? "#eff6fc"
      : "white",
    color: state.isSelected ? "white" : "#323130",
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 1000001,  // above Fluent UI Modal
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 1000001,  // was 9999 — must beat Fluent UI's 1000000
  }),
});

// ─── Component ────────────────────────────────────────────────────────────────

export function DepartmentSelector(props: departmentSelectorProps) {
  const [selectedDepartmentDetails, setSelectedDepartmentDetails] =
    useState<department>({});
  const [selectedDepartmentLv3, setSelectedDepartmentLv3] =
    useState<string>("");
  const [selectedDepartmentLv4, setSelectedDepartmentLv4] = useState<string>(
    props.departmentCode
  );
  const [selectedDepartmentChildren, setSelectedDepartmentChildren] = useState<
    Array<department>
  >([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { instance, accounts } = useMsal();
  const accessTokenRequest = {
    scopes: apiScopes.resourceManagementScopes,
    account: accounts[0],
  };

  function loadHierarchy(departmentCode: string) {
    instance
      .acquireTokenSilent(accessTokenRequest)
      .then(async (accessTokenResponse) => {
        const departmentResult = await getDepartment(
          accessTokenResponse.accessToken,
          departmentCode
        );
        if (departmentResult.length > 0) {
          setSelectedDepartmentDetails(departmentResult[0]);
          if (departmentResult[0].treeLevelNbr === 4) {
            setSelectedDepartmentLv3(
              departmentResult[0].parentDepartmentCd!
            );
          }
        }
        const departmentChildrenResult = await getDepartmentChildren(
          accessTokenResponse.accessToken,
          departmentCode
        );
        setSelectedDepartmentChildren(departmentChildrenResult);
      });
  }

  useEffect(() => {
    if (props.departmentCode) {
      loadHierarchy(props.departmentCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Options ──────────────────────────────────────────────────────────────

  const optionsLv3: OptionType[] = props.defaultDepartmentList
    .filter((f) => f.treeLevelNbr === 3)
    .map((dep) => ({
      label: dep.departmentNm as string,
      value: dep.departmentCd as string,
    }));

  const optionsLv4: OptionType[] = props.defaultDepartmentList
    .filter(
      (f) =>
        f.treeLevelNbr === 4 &&
        f.parentDepartmentCd === selectedDepartmentLv3
    )
    .map((dep) => ({
      label: dep.departmentNm as string,
      value: dep.departmentCd as string,
    }));

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleLv3Change = (newValue: SingleValue<OptionType>) => {
    setSaveSuccess(false);
    if (newValue) {
      setSelectedDepartmentLv3(newValue.value);
      const availableLv4 = props.defaultDepartmentList
        .filter(
          (f) =>
            f.treeLevelNbr === 4 &&
            f.parentDepartmentCd === newValue.value
        );
      if (availableLv4.length > 0) {
        const first = availableLv4[0];
        setSelectedDepartmentLv4(first.departmentCd as string);
        loadHierarchy(first.departmentCd as string);
      } else {
        setSelectedDepartmentLv4("");
        setSelectedDepartmentDetails({});
        setSelectedDepartmentChildren([]);
      }
    } else {
      setSelectedDepartmentLv3("");
      setSelectedDepartmentLv4("");
      setSelectedDepartmentDetails({});
      setSelectedDepartmentChildren([]);
    }
  };

  const handleLv4Change = (newValue: SingleValue<OptionType>) => {
    setSaveSuccess(false);
    if (newValue) {
      setSelectedDepartmentLv4(newValue.value);
      loadHierarchy(newValue.value);
    } else {
      setSelectedDepartmentLv4("");
      if (selectedDepartmentLv3) loadHierarchy(selectedDepartmentLv3);
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedDepartmentLv4?.trim()) {
      alert("Please select a sub department before saving changes.");
      return;
    }
    setIsSaving(true);
    try {
      await props.updateFunction(
        selectedDepartmentLv4,
        props.resourceId,
        props.isM365
      );
      setSaveSuccess(true);
    } finally {
      setIsSaving(false);
    }
  };

  const hasSelection = !!selectedDepartmentLv4?.trim();

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Wrapper>
      {/* Info Banner */}
      <InfoBanner>
        <Icon
          iconName="Info"
          style={{ color: "#0046c8", fontSize: 16, marginTop: 1, flexShrink: 0 }}
        />
        <div>
          <InfoBannerTitle>Department Assignment</InfoBannerTitle>
          <InfoBannerText>
            You can reassign this resource to a different department at any
            time. Select a <strong>Department</strong> and a{" "}
            <strong>Sub Department</strong>, then click{" "}
            <strong>Save Changes</strong> to apply. The org chart on the right
            previews the selected hierarchy before you save.
          </InfoBannerText>
        </div>
      </InfoBanner>

      {/* Success message */}
      {saveSuccess && (
        <MessageBar
          messageBarType={MessageBarType.success}
          isMultiline={false}
          onDismiss={() => setSaveSuccess(false)}
          dismissButtonAriaLabel="Close"
          style={{ marginBottom: 12, borderRadius: 6 }}
        >
          Department assignment saved successfully.
        </MessageBar>
      )}

      {/* Two-column layout */}
      <TwoColumnGrid>

        {/* ── LEFT: Form ── */}
        <SectionCard>
          <SectionTitle>
            <Icon iconName="Org" style={{ color: "#0046c8", fontSize: 13 }} />
            Select Department
          </SectionTitle>
          <SectionSubtitle>
            Choose the department and sub department this resource belongs to.
          </SectionSubtitle>

          <FieldWrapper>
            <FieldLabel htmlFor="departmentLv3">
              Department
              <TooltipHost content="Top-level department grouping (Level 3 in the org hierarchy).">
                <Icon
                  iconName="Info"
                  style={{ fontSize: 11, color: "#605e5c", cursor: "help" }}
                />
              </TooltipHost>
            </FieldLabel>
            <Select<OptionType>
              inputId="departmentLv3"
              value={
                optionsLv3.find((o) => o.value === selectedDepartmentLv3) ||
                null
              }
              isSearchable
              name="departmentLv3"
              options={optionsLv3}
              onChange={handleLv3Change}
              placeholder="Search departments..."
              styles={selectStyles()}
                menuPortalTarget={document.body}   
              menuPosition="fixed" 
            />
            <HelpText>Start typing to search all departments.</HelpText>
          </FieldWrapper>

          <FieldWrapper>
            <FieldLabel htmlFor="departmentLv4">
              Sub Department
              <TooltipHost content="Sub department within the selected department (Level 4 in the org hierarchy).">
                <Icon
                  iconName="Info"
                  style={{ fontSize: 11, color: "#605e5c", cursor: "help" }}
                />
              </TooltipHost>
            </FieldLabel>
            <Select<OptionType>
              inputId="departmentLv4"
              value={
                optionsLv4.find((o) => o.value === selectedDepartmentLv4) ||
                null
              }
              isSearchable
              onChange={handleLv4Change}
              name="departmentLv4"
              options={optionsLv4}
              placeholder={
                !selectedDepartmentLv3
                  ? "Select a department first..."
                  : "Search sub departments..."
              }
              isDisabled={!selectedDepartmentLv3}
              styles={selectStyles(!selectedDepartmentLv3)}
              menuPortalTarget={document.body}  
              menuPosition="fixed"  
            />
            <HelpText>
              {!selectedDepartmentLv3
                ? "A department must be selected first."
                : `${optionsLv4.length} sub department(s) available.`}
            </HelpText>
          </FieldWrapper>

          <Divider />

          <Stack
            horizontal
            verticalAlign="center"
            tokens={{ childrenGap: 10 }}
          >
            <PrimaryButton
              text={isSaving ? "Saving..." : "Save Changes"}
              onClick={handleSaveChanges}
              disabled={!hasSelection || isSaving}
              iconProps={{ iconName: "Save" }}
              styles={{ root: { borderRadius: 4, minWidth: 130 } }}
            />
          </Stack>
          {!hasSelection && (
            <HelpText style={{ marginTop: 8 }}>
              Select a sub department to enable saving.
            </HelpText>
          )}
        </SectionCard>

        {/* ── RIGHT: Org Chart ── */}
        <SectionCard>
          <SectionTitle>
            <Icon iconName="Org" style={{ color: "#0046c8", fontSize: 13 }} />
            Org Chart Preview
          </SectionTitle>
          <SectionSubtitle>
            The highlighted node (blue) is the currently selected sub
            department. Child departments are shown below it.
          </SectionSubtitle>

          <TreeLegendRow>
            <TreeLegendItem>
              <LegendDot bg="#fff" border="#0046c8" />
              Parent / child levels
            </TreeLegendItem>
            <TreeLegendItem>
              <LegendDot bg="#0046c8" border="#0046c8" />
              Selected sub department
            </TreeLegendItem>
          </TreeLegendRow>

          <TreeWrapper>
            <Tree
              lineWidth="2px"
              lineColor="#0046c8"
              lineBorderRadius="10px"
              label={
                <StyledNode>
                  {selectedDepartmentDetails.departmentLevelOneNm ||
                    "Organization"}
                </StyledNode>
              }
            >
              <TreeNode
                label={
                  <StyledNode>
                    {selectedDepartmentDetails.departmentLevelThreeNm ||
                      optionsLv3.find((o) => o.value === selectedDepartmentLv3)
                        ?.label ||
                      "Department"}
                  </StyledNode>
                }
              >
                <TreeNode
                  label={
                    <StyledNode
                      style={{
                        backgroundColor: "#0046c8",
                        color: "white",
                        fontWeight: 600,
                        borderColor: "#003399",
                      }}
                    >
                      {selectedDepartmentDetails.departmentLevelFourNm ||
                        optionsLv4.find(
                          (o) => o.value === selectedDepartmentLv4
                        )?.label ||
                        "Sub Department"}
                    </StyledNode>
                  }
                >
                  {selectedDepartmentChildren.length > 0 ? (
                    selectedDepartmentChildren.map((dep, index) => (
                      <TreeNode
                        key={dep.departmentCd || index}
                        label={
                          <StyledNode>{dep.departmentNm}</StyledNode>
                        }
                      />
                    ))
                  ) : (
                    <TreeNode
                      label={
                        <StyledNode
                          style={{
                            borderStyle: "dashed",
                            color: "#a19f9d",
                            fontSize: 11,
                          }}
                        >
                          No child departments
                        </StyledNode>
                      }
                    />
                  )}
                </TreeNode>
              </TreeNode>
            </Tree>
          </TreeWrapper>
        </SectionCard>
      </TwoColumnGrid>
    </Wrapper>
  );
}